import { User, TelegramWebApp } from '../types';
import { authService } from '../services/authService';

// Type declaration for window.Telegram
declare global {
  interface Window {
    Telegram?: {
      WebApp: TelegramWebApp;
    };
  }
}

export interface AppBootstrap {
  environment: 'telegram' | 'browser';
  hasValidToken: boolean;
  user: User | null;
  userStatus: 'unregistered' | 'pending' | 'approved' | 'rejected';
  targetRoute: string;
  authRequired: boolean;
  isAuthenticated: boolean;
  shouldRedirect: boolean;
}

/**
 * Early detection and validation system that determines user flow
 * This runs BEFORE React initialization to eliminate UI flicker
 */
export async function detectAndValidateEnvironment(): Promise<AppBootstrap> {
  console.log('🔍 Starting early environment detection...');
  
  // Phase 1: Detect environment (Telegram vs Browser)
  const environment = detectEnvironment();
  console.log('📱 Environment detected:', environment);
  
  // Phase 2: Check for existing valid token
  const existingToken = authService.getToken();
  const hasValidToken = existingToken && authService.isAuthenticated();
  console.log('🔑 Has valid token:', hasValidToken);
  
  // Phase 3: Validate token and get user data
  let user: User | null = null;
  let isAuthenticated = false;
  
  if (hasValidToken) {
    try {
      console.log('🔐 Validating token with backend...');
      const validationResult = await authService.validateToken();
      user = validationResult.user;
      isAuthenticated = true;
      console.log('✅ Token validation successful:', user.fullName, 'Status:', user.status);
    } catch (error) {
      console.log('❌ Token validation failed:', error);
      authService.clearToken();
    }
  }
  
  // Phase 4: Determine user status
  const userStatus = determineUserStatus(user);
  console.log('👤 User status:', userStatus);
  
  // Phase 5: Calculate target route based on environment and status
  const { targetRoute, authRequired } = calculateTargetRoute(environment, userStatus, isAuthenticated, user);
  console.log('🎯 Target route:', targetRoute, 'Auth required:', authRequired);
  
  const bootstrap: AppBootstrap = {
    environment,
    hasValidToken: !!hasValidToken,
    user,
    userStatus,
    targetRoute,
    authRequired,
    isAuthenticated,
    shouldRedirect: targetRoute !== '/' // Redirect if target route is not the default home page
  };
  
  console.log('🚀 Bootstrap complete:', bootstrap);
  return bootstrap;
}

/**
 * Detect if running in Telegram WebApp or regular browser
 */
function detectEnvironment(): 'telegram' | 'browser' {
  if (typeof window === 'undefined') return 'browser';
  
  const hasTelegramWebApp = window.Telegram && window.Telegram.WebApp;
  const hasValidInitData = hasTelegramWebApp &&
    window.Telegram?.WebApp?.initData && 
    window.Telegram.WebApp.initData.trim().length > 0;
  
  return hasTelegramWebApp && hasValidInitData ? 'telegram' : 'browser';
}

/**
 * Determine user registration and approval status
 */
function determineUserStatus(user: User | null): 'unregistered' | 'pending' | 'approved' | 'rejected' {
  if (!user) return 'unregistered';
  
  switch (user.status) {
    case -1: // New user
    case undefined:
      return user.needsRegistration ? 'unregistered' : 'pending';
    case 0: // Pending
      return 'pending';
    case 1: // Approved
      return 'approved';
    case 2: // Rejected
      return 'rejected';
    default:
      return 'unregistered';
  }
}

/**
 * Calculate target route and auth requirements based on user context
 * REUSES existing route paths and components
 */
function calculateTargetRoute(
  environment: 'telegram' | 'browser',
  userStatus: 'unregistered' | 'pending' | 'approved' | 'rejected',
  isAuthenticated: boolean,
  user: User | null
): { targetRoute: string; authRequired: boolean } {
  
  // Route decision matrix using EXISTING components and routes
  const routeKey = `${environment} + ${userStatus} + ${isAuthenticated ? 'authenticated' : 'unauthenticated'}`;
  
  console.log('🗺️ Route calculation for:', routeKey);
  
  switch (true) {
    // BROWSER USERS: All non-authenticated browser users go to /auth (PRIORITY RULE)
    case environment === 'browser' && !isAuthenticated:
      return { targetRoute: '/auth', authRequired: false }; // → AuthPage where user can login or register
    
    // TELEGRAM USERS: Handle based on status
    case environment === 'telegram' && userStatus === 'unregistered':
      return { targetRoute: '/', authRequired: false }; // → Layout → RegistrationPage (existing)
    
    case environment === 'telegram' && userStatus === 'approved' && !isAuthenticated:
      return { targetRoute: '/', authRequired: false }; // → Layout → auto-attempt Telegram auth
    
    case environment === 'telegram' && userStatus === 'pending':
      return { targetRoute: '/', authRequired: true }; // → Layout → pending approval screen
    
    case environment === 'telegram' && userStatus === 'rejected':
      return { targetRoute: '/', authRequired: true }; // → Layout → rejection screen
    
    // AUTHENTICATED USERS (both environments): Handle based on status
    case userStatus === 'approved' && isAuthenticated:
      return { targetRoute: '/', authRequired: true }; // → HomePage (existing)
    
    case userStatus === 'pending' && isAuthenticated:
      return { targetRoute: '/', authRequired: true }; // → Layout → pending approval screen (existing)
    
    case userStatus === 'rejected' && isAuthenticated:
      return { targetRoute: '/', authRequired: true }; // → Layout → rejection screen (existing)
    
    // Admin users - bypass most restrictions
    case user?.role === 2: // Admin
      return { targetRoute: '/', authRequired: true }; // → HomePage (existing)
    
    // Default fallback
    default:
      console.log('⚠️ Using fallback route for:', routeKey);
      return environment === 'telegram' 
        ? { targetRoute: '/', authRequired: false }
        : { targetRoute: '/auth', authRequired: false };
  }
}

// Global bootstrap state for use throughout the app
let globalBootstrap: AppBootstrap | null = null;

export function setGlobalBootstrap(bootstrap: AppBootstrap): void {
  globalBootstrap = bootstrap;
}

export function getGlobalBootstrap(): AppBootstrap | null {
  return globalBootstrap;
}